import React, { useContext, useEffect, useState } from 'react';
import {
  IRecipientList,
  IScheduler,
  ISender,
  ITemplate,
  useCreateSchedulerMutation,
  useGetAllSendersQuery,
  useRecipientListsQuery,
  useSendTestEmailMutation,
  useTemplatesQuery,
  useUpdateSchedulerMutation
} from '../../generated/graphql';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Types } from '../../enums/TypeEnums';
import { sameSchedulerNameExist, addSchedulerToCache, parseDate } from '../../utils/clientUtils';
import { canUserEdit } from '../../utils/rightUtils';
import { validEmailRegex } from '../../utils/ui';
import DialogBox from '../Common/Layout/DialogBox';
import { Box, Chip, Divider, InputBase } from '@mui/material';
import EditButton from '../Common/Buttons/EditButton';
import { Colors } from '../../utils/theme';
import InputComponent from '../Common/Fields/InputComponent';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import EpraTimePicker from '../Common/Fields/EpraTimePicker';
import { Info } from '@mui/icons-material';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';

interface IProps {
  handleClose: () => void;
  scheduler: IScheduler | null;
  isEditMode?: boolean;
}

interface IErrors {
  name: string | null;
  recipientLists: string | null;
  template: string | null;
  sender: string | null;
  date: string | null;
  time: string | null;
}

const defaultErrorsObj: IErrors = {
  name: null,
  recipientLists: null,
  template: null,
  sender: null,
  date: null,
  time: null
};

const ErrorTexts = {
  name: 'Job name too short',
  nameAlreadyExist: 'Job name already exists',
  recipientLists: 'Select Recipient List',
  template: 'Select Template',
  sender: 'Select Sender',
  date: 'Select date',
  dateInPast: 'Selected date should not be in the past',
  time: 'Select time',
  timeInPast: 'Selected time should not be in the past'
};

/** Build scheduled Date in the user's local timezone (picker hours = local clock). */
const buildLocalScheduledDate = (datePart: Date, timePart: Date): Date =>
  new Date(
    datePart.getFullYear(),
    datePart.getMonth(),
    datePart.getDate(),
    timePart.getHours(),
    timePart.getMinutes(),
    0,
    0
  );

const SchedulerForm: React.FunctionComponent<IProps> = ({
  handleClose,
  scheduler,
  isEditMode = false
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const recipientListsOptions = useRecipientListsQuery();
  const templates = useTemplatesQuery();
  const senders = useGetAllSendersQuery({ fetchPolicy: 'no-cache' });
  const [jobName, setJobName] = useState(scheduler?.name ?? '');
  const [date, setDate] = useState<Date | null>(
    isEditMode ? parseDate(scheduler?.scheduled_date) : null
  );
  const [time, setTime] = useState<Date | null>(
    isEditMode ? parseDate(scheduler?.scheduled_date) : null
  );
  const [recipientLists, setRecipientLists] = useState<IRecipientList[]>(
    (scheduler?.recipient_lists as IRecipientList[]) || []
  );
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>(
    scheduler?.template ?? null
  );
  const [selectedSender, setSelectedSender] = useState<ISender | null>(scheduler?.sender ?? null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createScheduler] = useCreateSchedulerMutation();
  const [updateScheduler] = useUpdateSchedulerMutation();
  const canEdit = canUserEdit(state.user, Types.Scheduler);
  const [editEnabled, setEditEnabled] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [sendTestScheduler, sendTestEmailResult] = useSendTestEmailMutation();

  useEffect(() => {
    if (scheduler && senders.data) {
      if (senders.data.result.findIndex((s) => s?.id === scheduler.sender.id) === -1) {
        setSelectedSender(null);
        setFormErrors({
          ...formErrors,
          sender: 'Sender has been removed. Please select another one'
        });
      }
    }
  }, [senders.data]);

  const handleJobNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value.length > 1) {
      setFormErrors({ ...formErrors, name: null });
    } else {
      setFormErrors({ ...formErrors, name: ErrorTexts.name });
    }
    setJobName(value);
  };

  const handleRecipientListChange = (_: unknown, recipientLists: IRecipientList[]) => {
    if (recipientLists.length > 0) {
      setFormErrors({ ...formErrors, recipientLists: null });
    } else {
      setFormErrors({ ...formErrors, recipientLists: ErrorTexts.recipientLists });
    }
    setRecipientLists(recipientLists);
  };

  const handleTemplateChange = (_: unknown, newValue: React.SetStateAction<ITemplate | null>) => {
    if (newValue !== null) {
      setFormErrors({ ...formErrors, template: null });
    } else {
      setFormErrors({ ...formErrors, template: ErrorTexts.template });
    }
    setSelectedTemplate(newValue);
  };

  const handleSenderChange = (_: unknown, newValue: React.SetStateAction<ISender | null>) => {
    if (newValue !== null) {
      setFormErrors({ ...formErrors, sender: null });
    } else {
      setFormErrors({ ...formErrors, sender: ErrorTexts.sender });
    }
    setSelectedSender(newValue);
  };

  const handleSave = () => {
    let hasErrors = false;
    const errs: IErrors = {
      name: null,
      recipientLists: null,
      date: null,
      sender: null,
      template: null,
      time: null
    };
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (jobName.length < 2) {
      hasErrors = true;
      errs.name = ErrorTexts.name;
    }
    if (recipientLists.length === 0) {
      hasErrors = true;
      errs.recipientLists = ErrorTexts.recipientLists;
    }
    if (selectedTemplate === null) {
      hasErrors = true;
      errs.template = ErrorTexts.template;
    }
    if (selectedSender === null) {
      hasErrors = true;
      errs.sender = ErrorTexts.sender;
    }
    if (date === null) {
      hasErrors = true;
      errs.date = ErrorTexts.date;
    } else if (date < now) {
      hasErrors = true;
      errs.date = ErrorTexts.dateInPast;
    }
    if (time === null) {
      hasErrors = true;
      errs.time = ErrorTexts.time;
    }
    if (date && time) {
      // Use local timezone (not Date.UTC) so the picked clock time matches the user's intent
      const selectedDate = buildLocalScheduledDate(date as Date, time as Date);
      const currentDate = new Date();
      if (currentDate.getTime() >= selectedDate.getTime()) {
        hasErrors = true;
        errs.time = ErrorTexts.timeInPast;
      }
    }
    if (sameSchedulerNameExist(jobName, isEditMode ? scheduler?.id : null)) {
      hasErrors = true;
      errs.name = ErrorTexts.nameAlreadyExist;
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'warning',
          text: 'A scheduler with the same name already exists',
          autoHide: 6000
        }
      });
    }
    if (hasErrors) {
      setFormErrors(errs);
    } else {
      submitScheduler();
    }
  };

  const submitScheduler = () => {
    // Local date/time → absolute instant (ISO). Previously Date.UTC treated picker hours as UTC,
    // so BG users had to schedule 3h early and UK (BST) users 1h early.
    const sc_date = buildLocalScheduledDate(date as Date, time as Date);
    const scheduledDateIso = sc_date.toISOString();
    if (isEditMode) {
      updateScheduler({
        variables: {
          id: parseInt(scheduler?.id as string),
          input: {
            name: jobName,
            recipient_lists: recipientLists.map((rl) => Number(rl.id)),
            template_id: parseInt(selectedTemplate?.id as string),
            sender_id: selectedSender?.id as string,
            scheduled_date: scheduledDateIso
          }
        },
        update() {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Successfully updated ${jobName}`,
              severity: 'success'
            }
          });
          handleClose();
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: e.message,
            severity: 'error',
            autoHide: 6000
          }
        });
      });
    } else {
      createScheduler({
        variables: {
          input: {
            name: jobName,
            recipient_lists: recipientLists.map((rl) => Number(rl.id)),
            template_id: parseInt(selectedTemplate?.id as string),
            sender_id: selectedSender?.id as string,
            scheduled_date: scheduledDateIso
          }
        },
        update(_, { data }) {
          if (data?.result) {
            addSchedulerToCache(data?.result as IScheduler);
          }
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Scheduler ${data?.result.name} created`,
              severity: 'success'
            }
          });
          handleClose();
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: e.message,
            severity: 'error',
            autoHide: 6000
          }
        });
      });
    }
  };

  const handleTimeChanged = (timeInput: Date | null) => {
    if (timeInput !== null) {
      setFormErrors({ ...formErrors, time: null });
      if (date) {
        const selectedDate = buildLocalScheduledDate(date as Date, timeInput as Date);
        const currentDate = new Date();
        if (currentDate.getTime() >= selectedDate.getTime()) {
          setFormErrors({ ...formErrors, time: ErrorTexts.timeInPast });
        }
      }
    } else {
      setFormErrors({ ...formErrors, time: ErrorTexts.time });
    }
    setTime(timeInput);
  };

  const handleDateChanged = (dateInput: Date | null) => {
    if (dateInput !== null) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      let errorDto = formErrors;
      if (dateInput < now) {
        errorDto = { ...errorDto, date: ErrorTexts.dateInPast };
        // setFormErrors({ ...formErrors, date: ErrorTexts.dateInPast });
      } else {
        errorDto = { ...errorDto, date: null };
        // setFormErrors({ ...formErrors, date: null });
      }

      if (time) {
        const selectedDate = buildLocalScheduledDate(dateInput as Date, time as Date);
        const currentDate = new Date();
        if (currentDate.getTime() >= selectedDate.getTime()) {
          errorDto = { ...errorDto, time: ErrorTexts.timeInPast };
          // setFormErrors({ ...formErrors, time: ErrorTexts.timeInPast });
        }
      }

      setFormErrors(errorDto);
    } else {
      setFormErrors({ ...formErrors, date: ErrorTexts.date });
    }
    setDate(dateInput);
  };

  const handleRecipientChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const trimmedValue = value.trim();
    setTestEmailRecipient(trimmedValue);
  };

  const chceckIfTestCanBeSend = () => {
    let result = false;
    if (
      selectedTemplate !== null &&
      selectedSender !== null &&
      testEmailRecipient.length > 1 &&
      testEmailRecipient.match(validEmailRegex)
    ) {
      result = true;
    }
    return result;
  };

  const onSendTestEmailButtonClicked = () => {
    if (chceckIfTestCanBeSend()) {
      sendTestScheduler({
        variables: {
          template_id: parseInt(selectedTemplate?.id as string),
          sender_id: selectedSender?.id as string,
          recipient: testEmailRecipient
        },
        update() {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Test email sent to ${testEmailRecipient}`,
              severity: 'success'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: e.message,
            severity: 'error',
            autoHide: 6000
          }
        });
      });
    }
  };

  const isTheSchedulerEditable = () => {
    if (!scheduler?.is_success) {
      return false;
    } else {
      return true;
    }
  };

  const takeRecipientListIds = (recipientLists: IRecipientList[]) => {
    const recipientListIds = recipientLists.map((rl) => rl.id);
    return recipientListIds;
  };
  return (
    <DialogBox
      id="scheduler-form"
      title="Scheduled Mass Email"
      width={690}
      height={880}
      onClose={handleClose}
    >
      {isEditMode && (
        <Box sx={{ position: 'absolute', right: '24px' }}>
          <EditButton
            id="scheduler-edit-button"
            disabled={editEnabled || isTheSchedulerEditable() || !canEdit || !scheduler}
            onClick={() => setEditEnabled(true)}
            editButtonColor={
              editEnabled || isTheSchedulerEditable() || !canEdit || !scheduler
                ? Colors.gray
                : Colors.skyBlue
            }
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: '37px', px: '7%', gap: '45px' }}>
        <InputComponent
          id={'job-name'}
          value={jobName}
          onChange={handleJobNameChange}
          errorMsg={formErrors.name}
          label={'Job Name'}
          disabled={isEditMode && !editEnabled}
        />
        <AutocompleteComponent
          id="recipient-list-autocomplete"
          label="Recipient List"
          options={
            (recipientListsOptions.data?.result.filter(
              (rl) =>
                !takeRecipientListIds(recipientLists as IRecipientList[]).includes(rl?.id as string)
            ) as never[]) || []
          }
          disabled={isEditMode && !editEnabled}
          errorMsg={formErrors.recipientLists}
          values={recipientLists}
          isSingle={false}
          onChange={handleRecipientListChange}
          getOptionLabel={(option: IRecipientList) => option.name.replace('_', ' ')}
          additionalStyle={{ gridColumn: 'span 4' }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              // eslint-disable-next-line react/jsx-key
              <Chip
                sx={{
                  backgroundColor: Colors.white,
                  color: Colors.main,
                  '& .MuiChip-deleteIcon': {
                    width: '20px',
                    height: '20px',
                    color: Colors.skyBlue,
                    '&:hover': {
                      color: Colors.main
                    }
                  }
                }}
                variant="outlined"
                label={`${option.name.replace('_', ' ')}`}
                {...getTagProps({ index })}
              />
            ))
          }
        />
        <AutocompleteComponent
          id="template-autocomplete"
          label="Template"
          options={(templates.data?.result as ITemplate[]) ?? []}
          getOptionLabel={(option) => option.title}
          values={selectedTemplate}
          onChange={handleTemplateChange}
          errorMsg={formErrors.template}
          disabled={isEditMode && !editEnabled}
        />
        <AutocompleteComponent
          id="sender-autocomplete"
          label="Sender"
          options={(senders.data?.result as ISender[]) ?? []}
          getOptionLabel={(option) => option.email}
          values={selectedSender}
          onChange={handleSenderChange}
          errorMsg={formErrors.sender}
          disabled={isEditMode && !editEnabled}
        />
        <Divider />
        <Box sx={{ display: 'flex', gap: '15px' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="scheduler-date-picker"
              label="Date"
              value={date}
              onChange={(d) => handleDateChanged(d)}
              disabled={isEditMode && !editEnabled}
              minDate={new Date()}
              errorMsg={formErrors.date}
              sx={{
                width: '70%'
              }}
            />
            <EpraTimePicker
              id="scheduler-time-picker"
              label="Job Time"
              value={time}
              onChange={(t) => handleTimeChanged(t)}
              disabled={isEditMode && !editEnabled}
              errorMsg={formErrors.time}
              sx={{
                width: '70%'
              }}
            />
            <InputBase
              id="date-time-info"
              data-testid="date-time-info"
              value={`Based on your local time`}
              disabled
              fullWidth
              startAdornment={
                <Info
                  sx={{ width: '17px', height: '17px', marginTop: '-3px', paddingRight: '3px' }}
                />
              }
              inputProps={{
                style: {
                  fontSize: '12px',
                  fontFamily: 'Overpass'
                }
              }}
            />
          </LocalizationProvider>
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <InputComponent
            id="test-email-recipient"
            value={testEmailRecipient}
            onChange={handleRecipientChange}
            label={'Test Email Recipient'}
          />
          <CancelButton
            id="send-test-email-button"
            disabled={chceckIfTestCanBeSend() ? false : true}
            onClick={onSendTestEmailButtonClicked}
            text="Send Test Email"
            sx={{
              width: '200px',
              '&:hover': {
                width: '200px'
              }
            }}
          />
        </Box>
      </Box>
      <Box className="centralize" sx={{ gridColumn: 'span 3', mt: '140px' }}>
        <CancelButton id="cancel-scheduler-button" onClick={handleClose} text="Cancel" />
        <SaveButton
          id="create-template-button"
          onClick={handleSave}
          text={isEditMode ? 'Save' : 'Create'}
          disabled={isEditMode && !editEnabled}
        />
      </Box>
    </DialogBox>
  );
};

export default SchedulerForm;
